// API клиент для работы с VIDCREATE Backend (из orig/main.py)

import axios, { AxiosInstance } from 'axios';
import type {
  Project,
  ApiResponse,
  VideoSettings,
  LipsyncSettings,
  FirstFrameSettings,
  BackgroundTask,
  SceneHistory,
  VoiceGeneration,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_URL = `${API_BASE_URL}/api`;

// Создаем axios instance с настройками
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерсептор для обработки ошибок
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Перенаправляем на логин при 401
      window.location.href = '/manager/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTH API
// ============================================

export const authApi = {
  login: async (username: string, password: string): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/login', { username, password });
    return response.data;
  },

  logout: async (): Promise<ApiResponse> => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  check: async (): Promise<ApiResponse> => {
    const response = await apiClient.get('/auth/check');
    return response.data;
  },
};

// ============================================
// PROJECTS API
// ============================================

export const projectsApi = {
  list: async (): Promise<Project[]> => {
    const response = await apiClient.get('/projects/');
    return response.data.projects || [];
  },

  get: async (projectId: number | string): Promise<Project> => {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  },

  create: async (name: string): Promise<ApiResponse> => {
    const response = await apiClient.post('/projects/', { name });
    return response.data;
  },

  delete: async (projectId: number | string): Promise<ApiResponse> => {
    const response = await apiClient.post(`/projects/${projectId}/delete`);
    return response.data;
  },
};

// ============================================
// SCENES API
// ============================================

export const scenesApi = {
  edit: async (projectId: string, sceneNumber: number, prompt: string): Promise<ApiResponse> => {
    const response = await apiClient.post(`/projects/${projectId}/scenes/${sceneNumber}/edit`, {
      prompt,
    });
    return response.data;
  },

  reset: async (projectId: string, sceneNumber: number): Promise<ApiResponse> => {
    const response = await apiClient.post(`/projects/${projectId}/scenes/${sceneNumber}/reset`);
    return response.data;
  },

  deleteCustom: async (projectId: string, sceneNumber: number): Promise<ApiResponse> => {
    const response = await apiClient.delete(
      `/projects/${projectId}/scenes/${sceneNumber}/delete`
    );
    return response.data;
  },

  history: async (projectId: string, sceneNumber: number): Promise<SceneHistory> => {
    const response = await apiClient.get(
      `/projects/${projectId}/scenes/${sceneNumber}/history`
    );
    return response.data;
  },

  // New Composition
  newComposition: async (
    projectId: string,
    sceneNumber: number,
    data: {
      character_keys: string[];
      environment_key: string;
      prompt: string;
    }
  ): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/scenes/${sceneNumber}/new-composition`,
      data
    );
    return response.data;
  },

  // Edit Image
  editImage: async (
    projectId: string,
    sceneNumber: number,
    data: {
      model: string;
      prompt: string;
      image_url: string;
    }
  ): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/scenes/${sceneNumber}/edit-image`,
      data
    );
    return response.data;
  },


  // Generate Video
  generateVideo: async (
    projectId: string,
    sceneNumber: number,
    settings: VideoSettings
  ): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/scenes/${sceneNumber}/generate-video`,
      settings
    );
    return response.data;
  },

  // Generate LipSync
  generateLipsync: async (
    projectId: string,
    sceneNumber: number,
    settings: LipsyncSettings
  ): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/scenes/${sceneNumber}/generate-lipsync`,
      settings
    );
    return response.data;
  },

  // Voice History
  voiceHistory: async (projectId: string, sceneNumber: number): Promise<VoiceGeneration[]> => {
    const response = await apiClient.get(
      `/projects/${projectId}/scenes/${sceneNumber}/voice-history`
    );
    return response.data;
  },

  // Generate Voice
  generateVoice: async (
    projectId: string,
    sceneNumber: number,
    data: {
      character_key: string;
      text: string;
    }
  ): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/scenes/${sceneNumber}/generate-voice`,
      data
    );
    return response.data;
  },
};

// ============================================
// CHARACTERS API
// ============================================

export const charactersApi = {
  edit: async (projectId: string, characterKey: string, prompt: string): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/characters/${characterKey}/edit`,
      { prompt }
    );
    return response.data;
  },

  generate: async (projectId: string, characterKey: string): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/characters/${characterKey}/generate`
    );
    return response.data;
  },

  checkVoice: async (
    projectId: string,
    characterKey: string
  ): Promise<{ has_voice: boolean; model_name?: string }> => {
    const response = await apiClient.get(
      `/projects/${projectId}/characters/${characterKey}/check-voice`
    );
    return response.data;
  },

  trainVoice: async (
    projectId: string,
    characterKey: string,
    audioFile: File
  ): Promise<ApiResponse> => {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await apiClient.post(
      `/projects/${projectId}/characters/${characterKey}/train-voice`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};

// ============================================
// ENVIRONMENTS API
// ============================================

export const environmentsApi = {
  edit: async (
    projectId: string,
    environmentKey: string,
    prompt: string
  ): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/environments/${environmentKey}/edit`,
      { prompt }
    );
    return response.data;
  },

  generate: async (projectId: string, environmentKey: string): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/environments/${environmentKey}/generate`
    );
    return response.data;
  },
};

// ============================================
// IMAGE GENERATIONS API
// ============================================

export const imageGenerationsApi = {
  setPrimary: async (projectId: string, generationId: number): Promise<ApiResponse> => {
    const response = await apiClient.post(
      `/projects/${projectId}/image-generations/${generationId}/set-primary`
    );
    return response.data;
  },

  delete: async (projectId: string, generationId: number): Promise<ApiResponse> => {
    const response = await apiClient.delete(
      `/projects/${projectId}/image-generations/${generationId}`
    );
    return response.data;
  },
};

// ============================================
// LIPSYNC GENERATIONS API
// ============================================

export const lipsyncGenerationsApi = {
  delete: async (projectId: string, generationId: number): Promise<ApiResponse> => {
    const response = await apiClient.delete(
      `/projects/${projectId}/lipsync-generations/${generationId}`
    );
    return response.data;
  },
};

// ============================================
// VOICE GENERATIONS API
// ============================================

export const voiceGenerationsApi = {
  delete: async (projectId: string, generationId: number): Promise<ApiResponse> => {
    const response = await apiClient.delete(
      `/projects/${projectId}/voice-generations/${generationId}`
    );
    return response.data;
  },
};

// ============================================
// TASKS API
// ============================================

export const tasksApi = {
  getStatus: async (taskId: string, projectId?: number): Promise<BackgroundTask> => {
    const url = projectId
      ? `/tasks/${taskId}?project_id=${projectId}`
      : `/tasks/${taskId}`;
    const response = await apiClient.get(url);
    return response.data;
  },
};

// ============================================
// UPLOAD API
// ============================================

export const uploadApi = {
  uploadTempFile: async (file: File): Promise<{ success: boolean; url: string; error?: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/upload-temp-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================
// SCENARIOS API
// ============================================

export const scenariosApi = {
  get: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/scenarios/${projectId}`);
    return response.data;
  },

  save: async (projectId: number, scenario: any): Promise<ApiResponse> => {
    const response = await apiClient.post(`/scenarios/${projectId}`, scenario);
    return response.data;
  },

  analyze: async (projectId: number, videoUrl: string): Promise<ApiResponse> => {
    const response = await apiClient.post('/scenarios/analyze', {
      project_id: projectId,
      video_url: videoUrl
    });
    return response.data;
  },
};

// ============================================
// IMAGES API
// ============================================

export const imagesApi = {
  generate: async (data: {
    project_id: number;
    scene_number: number;
    model: string;
    prompt: string;
    settings?: any;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/images/generate', data);
    return response.data;
  },

  edit: async (data: {
    project_id: number;
    scene_number: number;
    model: string;
    prompt: string;
    image_url: string;
    settings?: any;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/images/edit', data);
    return response.data;
  },

  generateFirstFrame: async (data: {
    project_id: number;
    scene_number: number;
    character_key?: string;
    character_reference_prompt?: string;
    environment_prompt: string;
    first_frame_description: string;
    model?: string;
    color_grading?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/images/first-frame', data);
    return response.data;
  },
};

// ============================================
// VIDEOS API
// ============================================

export const videosApi = {
  generate: async (data: {
    project_id: number;
    scene_number: number;
    model: string;
    prompt: string;
    clip_type?: string;
    settings?: any;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/videos/generate', data);
    return response.data;
  },

  generateLipsync: async (data: {
    project_id: number;
    scene_number: number;
    model?: string;
    video_url: string;
    audio_url: string;
    settings?: any;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/videos/lipsync', data);
    return response.data;
  },
};

// ============================================
// VOICES API
// ============================================

export const voicesApi = {
  generate: async (data: {
    project_id: number;
    scene_number: number;
    character_key: string;
    text: string;
    voice_id?: string;
    settings?: any;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post('/voices/generate', data);
    return response.data;
  },

  clone: async (formData: FormData): Promise<ApiResponse> => {
    const response = await apiClient.post('/voices/clone', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  list: async (): Promise<{ voices: any[] }> => {
    const response = await apiClient.get('/voices/list');
    return response.data;
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Конвертирует относительный URL в абсолютный с backend доменом
 * Например: /static/projects/1/videos/video.mp4 -> https://vidcreate.golive.mobi/api/static/projects/1/videos/video.mp4
 */
export function getFullUrl(url: string | undefined): string {
  if (!url) return '';

  // Если URL уже абсолютный (начинается с http:// или https://), возвращаем как есть
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Исправить старые URL: /static/... -> /api/static/...
  let fixedUrl = url;
  if (url.startsWith('/static/')) {
    fixedUrl = '/api' + url;
  }

  // Если относительный URL, добавляем базовый URL
  const baseUrl = API_BASE_URL || '';

  // Убираем двойные слэши
  const fullUrl = baseUrl + (fixedUrl.startsWith('/') ? fixedUrl : '/' + fixedUrl);

  return fullUrl;
}

/**
 * Добавляет timestamp к URL для обхода кэша
 */
export function addTimestampToUrl(url: string | undefined): string {
  if (!url) return '';
  const fullUrl = getFullUrl(url);
  const separator = fullUrl.includes('?') ? '&' : '?';
  return fullUrl + separator + 't=' + Date.now();
}

/**
 * Получает полный URL со timestamp (комбинация обеих функций)
 */
export function getFullUrlWithTimestamp(url: string | undefined): string {
  return addTimestampToUrl(url);
}

export default apiClient;
